import { Form } from 'antd';
import type { NamePath } from 'rc-field-form/es/interface';
import type { ReactNode } from 'react';
import { useContext } from 'react';
import { useStore } from 'zustand';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { formatDateWithoutHour } from 'app/utils/dates';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { inventoryStore } from '../../InventoryStore';
import type { DuplicationModeData, FormPlanning } from '../../type';
import { getDatesToFill } from '../../utils';

import type { CellIconProps } from './_components/CellIcon';
import { CheckboxCell } from './_components/CheckboxCell';
import { EditableCell } from './_components/EditableCell';
import { ReadOnlyCell } from './_components/ReadOnlyCell';

type InventoryCellProps = {
  nameField?: NamePath;
  date: Date;
  roomId: number;
  packageId?: number;
  name?: string;
  datePlanning: FormPlanning;
  getDescriptionValue?: (
    datePlanning: FormPlanning
  ) => number | string | ReactNode;
  isEditable: boolean;
  getValue: (datePlanning: FormPlanning) => number | boolean | string;
  isEditMode: boolean;
  disabled?: boolean;
  getIconParameters?: (datePlanning: FormPlanning) => CellIconProps;
  tooltipMessage?: ReactNode;
  getError?: (datePlanning: FormPlanning) => string | undefined;
};

export const InventoryCell = ({
  getValue,
  getDescriptionValue,
  name,
  date,
  nameField,
  isEditable = true,
  datePlanning,
  roomId,
  packageId,
  isEditMode,
  disabled = false,
  getIconParameters,
  tooltipMessage,
  getError,
}: InventoryCellProps) => {
  const handleDuplicate = useStore(
    inventoryStore,
    (state) => state.handleDuplicate
  );
  const timePeriod = useStore(inventoryStore, (state) => state.timePeriod);
  const form = Form.useFormInstance();
  const { hotelInfo } = useContext(HotelContext);
  const { packages, getExperiencesForPackage, hasAtLeastOneDayPackage } =
    useContext(LegacyHotelContext);

  if (isEditable && isEditMode) {
    if (name === 'published' || name === 'closed') {
      const forcedName =
        nameField && Array.isArray(nameField)
          ? [...nameField.slice(0, -1), 'isForced']
          : nameField;

      return (
        <>
          {name === 'published' && (
            <Form.Item noStyle name={forcedName}>
              <input className="empty-input" />
            </Form.Item>
          )}
          <CheckboxCell date={date} nameField={nameField} disabled={disabled} />
        </>
      );
    }

    const tryDuplicate = (modeData: DuplicationModeData) => {
      const datesToFill = getDatesToFill(date, modeData, timePeriod);

      amplitudeTrack('Duplicate line', {
        from: formatDateWithoutHour(new Date(datesToFill.start)),
        to: formatDateWithoutHour(new Date(datesToFill.end)),
        line: name,
        duplicationType: modeData.mode,
      });

      handleDuplicate &&
        name &&
        nameField &&
        roomId &&
        handleDuplicate(
          {
            property: name,
            value: form.getFieldValue(nameField),
            day: date,
            modeData,
            roomId,
            packageId,
            nameField,
          },
          packages,
          getExperiencesForPackage,
          hotelInfo?.roomCount || 0,
          hasAtLeastOneDayPackage
        );
    };

    return (
      <EditableCell
        date={date}
        nameField={nameField}
        hasDuplication
        descriptionValue={getDescriptionValue?.(datePlanning)}
        handleDuplicate={tryDuplicate}
        disabled={disabled}
        iconParameters={getIconParameters?.(datePlanning)}
        tooltipMessage={tooltipMessage}
        error={getError?.(datePlanning)}
      />
    );
  }

  return (
    <ReadOnlyCell
      value={getValue(datePlanning)}
      descriptionValue={getDescriptionValue?.(datePlanning)}
      iconParameters={getIconParameters?.(datePlanning)}
      tooltipMessage={tooltipMessage}
    />
  );
};
